import { ImageLightbox } from "./ImageLightbox";

/** Une paire label/valeur affichée dans le corps de la fiche (voir `EntityPanel`). */
export interface EntityPanelField {
  label: string;
  value: string;
  /** `image` : `value` est l'URL d'une image, affichée plutôt qu'écrite. `color` : une couleur CSS,
   * écrite et précédée d'une pastille. `url` : un lien, ouvert dans un nouvel onglet. */
  kind?: "text" | "image" | "color" | "url";
}

/**
 * Contenu d'une fiche entité — `ui/EntityPanel` ne connaît ni `Shape` ni `Shape.entity` (frontière
 * core/adapter, voir CLAUDE.md) : c'est `createTokaMap.ts` qui construit cet objet à partir de
 * `TokaMapConfig.entityPanel.getEntityPanelEntry`, même principe que `SearchBoxEntry`/`getSearchItem`.
 */
export interface EntityPanelEntry {
  title: string;
  subTitle?: string;
  image?: string;
  fields?: EntityPanelField[];
}

/**
 * Actions affichées sous le contenu de la fiche — distinctes d'`EntityPanelEntry` (données de
 * l'entité, fournies par `TokaMapConfig.entityPanel.getEntityPanelEntry`) : construites par
 * `createTokaMap.ts` lui-même, qui seul sait si le mode itinéraire est configuré et si la
 * géolocalisation est active (voir `TokaMapInstance.startRouteMode`, CLAUDE.md "Itinéraire /
 * pathfinding"). `onGoTo` absent : aucun bouton "Y aller" affiché.
 */
export interface EntityPanelActions {
  onGoTo?: () => void;
}

/** Seules les adresses http(s) deviennent un lien : un `javascript:` saisi dans le champ s'exécuterait. */
function isWebUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

/** En dessous de ce déplacement (px), un glisser sur la poignée est traité comme un simple clic
 * (ferme la fiche) plutôt que comme un vrai redimensionnement — voir `setupHandleDrag`. */
const HANDLE_TAP_THRESHOLD_PX = 6;

/**
 * Fiche d'information affichée au clic sur une forme liée à une entité, en mode visualisation
 * seule (`editMode: false` — voir `TokaMapConfig.entityPanel`, `createTokaMap.ts`) : logo/nom/
 * sous-titre + une liste de champs label/valeur. Purement en lecture, pas d'édition possible ici
 * (contrairement à `packages/example/src/Sidebar.tsx`, réservé au mode édition).
 *
 * Responsive en CSS pur (voir `.toka-entity-panel` dans `style.css`) : bottom sheet sous un
 * breakpoint, panneau flottant à gauche au-dessus — breakpoint basé sur la largeur de `container`
 * (via `@container`, pas `@media`), pas celle du viewport : la carte peut être intégrée dans une
 * mise en page où elle n'occupe qu'une partie de la fenêtre (ex. à côté d'une sidebar), auquel cas
 * c'est l'espace réellement disponible pour la carte qui doit décider du layout, pas la largeur de
 * l'écran. `container-type: inline-size` posé ci-dessous est ce qui établit `container` comme
 * contexte d'interrogation pour les `@container` de `style.css` — sans ça `@container` ne
 * regarderait aucun ancêtre et ne matcherait jamais. La poignée (`__handle`) reste posée en JS sur
 * les deux layouts, mais n'a d'effet visible qu'en bottom sheet : masquée par CSS en layout
 * desktop, un élément `display: none` ne reçoit aucun évènement pointeur, donc `setupHandleDrag`
 * n'y fait jamais rien.
 */
export class EntityPanel {
  private element: HTMLDivElement | null = null;
  private lightbox: ImageLightbox;

  constructor(
    private container: HTMLElement,
    private onInsetsChange?: (insets: { left: number; bottom: number } | null) => void,
    private onClose?: () => void,
  ) {
    this.container.style.containerType = "inline-size";
    this.lightbox = new ImageLightbox(container);
  }

  show(entry: EntityPanelEntry, actions?: EntityPanelActions): void {
    this.close();

    const panel = document.createElement("div");
    panel.className = "toka-entity-panel";
    // Démarre hors écran (voir `.toka-entity-panel--hidden`, style.css) puis retiré au frame
    // suivant pour laisser le navigateur animer la transition plutôt que sauter à l'état final.
    panel.classList.add("toka-entity-panel--hidden");

    // Poignée décorative + zone de glisser (voir img_1.png) — masquée en layout panneau desktop
    // via `style.css`, voir `setupHandleDrag`.
    const handle = document.createElement("div");
    handle.className = "toka-entity-panel__handle";
    panel.appendChild(handle);

    const header = document.createElement("div");
    header.className = "toka-entity-panel__header";

    if (entry.image) {
      const image = document.createElement("img");
      image.className = "toka-entity-panel__image";
      image.src = entry.image;
      image.alt = "";
      header.appendChild(image);
    }

    const text = document.createElement("div");
    text.className = "toka-entity-panel__text";

    const title = document.createElement("div");
    title.className = "toka-entity-panel__title";
    title.textContent = entry.title;
    text.appendChild(title);

    if (entry.subTitle) {
      const subTitle = document.createElement("div");
      subTitle.className = "toka-entity-panel__subtitle";
      subTitle.textContent = entry.subTitle;
      text.appendChild(subTitle);
    }
    header.appendChild(text);

    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.className = "toka-entity-panel__close";
    closeButton.textContent = "×";
    closeButton.setAttribute("aria-label", "Fermer");
    closeButton.addEventListener("click", () => this.close());
    header.appendChild(closeButton);

    panel.appendChild(header);

    if (entry.fields?.length) {
      const fields = document.createElement("dl");
      fields.className = "toka-entity-panel__fields";
      // Les images qui se suivent partagent une rangée, et passent à la ligne faute de place.
      let imageRow: HTMLDivElement | null = null;
      for (const field of entry.fields) {
        const dt = document.createElement("dt");
        dt.textContent = field.label;

        const dd = document.createElement("dd");

        if (field.kind === "image") {
          const image = document.createElement("img");
          image.className = "toka-entity-panel__field-image";
          image.src = field.value;
          image.alt = field.label;
          image.addEventListener("click", () => this.lightbox.open(field.value, field.label));
          dd.appendChild(image);

          if (!imageRow) {
            imageRow = document.createElement("div");
            imageRow.className = "toka-entity-panel__image-row";
            fields.appendChild(imageRow);
          }

          const item = document.createElement("div");
          item.append(dt, dd);
          imageRow.appendChild(item);
          continue;
        }

        imageRow = null;
        if (field.kind === "color") {
          dd.classList.add("toka-entity-panel__color");
          const swatch = document.createElement("span");
          swatch.className = "toka-entity-panel__color-swatch";
          swatch.style.background = field.value;
          dd.append(swatch, field.value);
        } else if (field.kind === "url" && isWebUrl(field.value)) {
          const link = document.createElement("a");
          link.className = "toka-entity-panel__link";
          link.href = field.value;
          link.target = "_blank";
          link.rel = "noopener noreferrer";
          link.textContent = field.value;
          dd.appendChild(link);
        } else {
          dd.textContent = field.value;
        }
        fields.append(dt, dd);
      }
      panel.appendChild(fields);
    }

    if (actions?.onGoTo) {
      const goToButton = document.createElement("button");
      goToButton.type = "button";
      goToButton.className = "toka-entity-panel__go-to";
      // Icône "compass" (voir https://lucide.dev/icons/compass, ISC) recopiée en inline plutôt
      // qu'importée d'une lib d'icônes : `@tokamap/core` n'a aujourd'hui aucune dépendance UI/icônes
      // (voir `package.json`), une seule icône ne justifie pas d'en ajouter une pour ce bouton.
      goToButton.innerHTML =
        '<svg class="toka-entity-panel__go-to-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
        'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<circle cx="12" cy="12" r="10"/>' +
        '<polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>' +
        "</svg>" +
        "<span>Y aller</span>";
      goToButton.addEventListener("click", () => actions.onGoTo!());
      panel.appendChild(goToButton);
    }

    this.container.appendChild(panel);
    this.element = panel;
    this.setupHandleDrag(panel, handle, header);
    this.onInsetsChange?.(this.getOccupiedInsets());

    requestAnimationFrame(() => panel.classList.remove("toka-entity-panel--hidden"));
  }

  /**
   * Marges (en pixels, relatives à `container`) actuellement occupées par la fiche affichée — `null`
   * si aucune. À combiner avec `MapAdapter.ensureShapeVisible` (voir `createTokaMap.ts`, même
   * usage que `packages/example/src/Sidebar.tsx`/`App.tsx` pour leur propre panneau) pour garder la
   * forme cliquée visible si la fiche vient de s'ouvrir par-dessus elle. Lue sur `offsetLeft`/
   * `offsetTop`/`offsetWidth`/`offsetHeight` (jamais affectés par une `transform`, contrairement à
   * `getBoundingClientRect()`) : reste correcte même pendant l'animation d'entrée (voir `show()`).
   *
   * Un seul bord à la fois : les deux layouts occupent des bords DIFFÉRENTS (bottom sheet : le bas,
   * pleine largeur ; panneau desktop : la gauche, pleine hauteur), jamais les deux en même temps —
   * retourner systématiquement `left` ET `bottom` compterait la hauteur du panneau desktop (qui
   * s'étend presque jusqu'en bas) comme une marge "bas" en plus de sa marge "gauche", écrasant la
   * zone visible restante vers un mince bandeau en haut de l'écran. Distingue les deux via la
   * largeur occupée (une bottom sheet couvre presque toute la largeur du conteneur, un panneau
   * desktop est nettement plus étroit) plutôt que de dupliquer le breakpoint CSS ici — s'adapte
   * de lui-même au layout actuellement actif.
   */
  getOccupiedInsets(): { left: number; bottom: number } | null {
    const panel = this.element;
    if (!panel) return null;
    const isFullWidthSheet = panel.offsetWidth >= this.container.clientWidth * 0.9;
    return {
      left: isFullWidthSheet ? 0 : panel.offsetLeft + panel.offsetWidth,
      bottom: isFullWidthSheet ? this.container.clientHeight - panel.offsetTop : 0,
    };
  }

  close(): void {
    this.lightbox.close();

    const panel = this.element;
    if (!panel) return;
    this.element = null;
    this.onInsetsChange?.(null);
    this.onClose?.();

    panel.classList.add("toka-entity-panel--hidden");
    panel.addEventListener("transitionend", () => panel.remove(), { once: true });
    // Filet de sécurité si la transition ne se déclenche jamais (ex. `prefers-reduced-motion`,
    // voir style.css) : le retrait ne doit pas dépendre uniquement de l'évènement `transitionend`.
    setTimeout(() => panel.remove(), 300);
  }

  /**
   * Glisser la poignée redimensionne la bottom sheet en direct (suit le doigt/curseur, bornée entre
   * la hauteur de l'en-tête et la hauteur "pleine"). Au relâchement : un déplacement quasi nul
   * (`HANDLE_TAP_THRESHOLD_PX`, un simple clic sur la poignée) ou une hauteur finale sous la moitié
   * de la hauteur pleine ferment la fiche (`close()`, même animation de sortie que le bouton `×`) ;
   * sinon elle revient à sa hauteur pleine — pas d'état intermédiaire "repliée" persistant, cf.
   * retour utilisateur (glisser doit fermer, pas juste réduire). Un seul jeu d'écouteurs
   * (pointerdown/move/up), pas de `click` séparé, pour éviter tout double-déclenchement (un `click`
   * de navigateur suit toujours un `pointerup`, sur la même cible).
   */
  private setupHandleDrag(panel: HTMLDivElement, handle: HTMLDivElement, header: HTMLDivElement): void {
    const getBounds = () => ({
      min: header.offsetHeight,
      full: Math.round(this.container.clientHeight * 0.7),
    });

    let dragStartClientY = 0;
    let panelStartHeight = 0;

    const onPointerMove = (event: PointerEvent): void => {
      const bounds = getBounds();
      const deltaY = event.clientY - dragStartClientY;
      const nextHeight = Math.min(bounds.full, Math.max(bounds.min, panelStartHeight - deltaY));
      panel.style.height = `${nextHeight}px`;
    };

    const onPointerUp = (event: PointerEvent): void => {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      panel.classList.remove("toka-entity-panel--dragging");

      const bounds = getBounds();
      const totalDelta = Math.abs(event.clientY - dragStartClientY);
      const isTap = totalDelta < HANDLE_TAP_THRESHOLD_PX;
      if (isTap || panel.getBoundingClientRect().height < bounds.full / 2) {
        this.close();
        return;
      }
      panel.style.height = `${bounds.full}px`;
    };

    handle.addEventListener("pointerdown", (event) => {
      dragStartClientY = event.clientY;
      panelStartHeight = panel.getBoundingClientRect().height;
      panel.classList.add("toka-entity-panel--dragging");
      document.addEventListener("pointermove", onPointerMove);
      document.addEventListener("pointerup", onPointerUp);
    });
  }
}
